import axios from 'axios'
import { sm2 } from 'sm-crypto'

/**
 * 招生问答 API - 通用后端对接
 *
 * 后端接口约定：
 * - 方式：POST
 * - 路径：/api/zaosheng/chat 或你在环境变量里配置的路径
 * - 请求体：{ "question": "用户输入的问题" }
 * - 响应体（常见封装）：{ "code": 200, "data": { "answer": "回复内容" } }
 *   或直接：{ "answer": "回复内容" }
 * - 鉴权：若接口需 Token，在 requestHeaders 里加 Authorization 等
 */

/** 后端 API 配置：通过 import.meta.env 获取环境变量 */
const rawBaseURL = import.meta.env.VITE_API_BASE_URL || ''
const devBaseURL = import.meta.env.DEV ? '' : rawBaseURL
export const apiConfig = {
  baseURL: devBaseURL,
  apiConfigured: !!rawBaseURL,
  chatPath: import.meta.env.VITE_API_CHAT_PATH || '/api/ai/selfAgent/aiAssistantAnswer',
  agentId: import.meta.env.VITE_AGENT_ID || '',
  eventAgentId: import.meta.env.VITE_EVENT_AGENT_ID || '',
  token: '',
  rawToken: '',
  forwardedHost: import.meta.env.VITE_FORWARDED_HOST || '',
}

/** 新问答接口配置（统一走后端 /tts-api 代理） */
const newApiConfig = {
  streamPath: import.meta.env.VITE_NEW_API_URL || 'http://hxai.sundeinfo.com/tts-api/qa-stream',
  titleRewritePath: import.meta.env.VITE_NEW_API_BASE
    ? `${import.meta.env.VITE_NEW_API_BASE.replace(/\/$/, '')}/title-rewrite`
    : 'https://hxai.sundeinfo.com/tts-api/title-rewrite',
  assistantId: import.meta.env.VITE_NEW_ASSISTANT_ID || '1992229605230174209',
  forwardedHost: import.meta.env.VITE_NEW_FORWARDED_HOST || 'hangkong',
  backendUrl: import.meta.env.VITE_NEW_BACKEND_URL || 'https://hxai.sundeinfo.com/api',
  systemPrompt: `你是上海市航空服务学校的AI招生助手。请以专业、耐心、亲和、清晰的方式回答学生和家长关于招生的问题。

回答要求：
1. 学校名称必须始终使用"上海市航空服务学校"，不得简称。
2. 回答要先给明确结论，再补充说明，仅精确简单地回答问题相关的内容。
3. 招生代码、学制、招生范围、面试要求等内容，必须严格依据已提供资料回答，不得编造。
4. 不做保证性承诺，如"100%录取""一定包就业"。
5. 若资料中没有明确说明，统一回复："目前学校提供资料中没有明确说明，建议联系上海市航空服务学校招生老师进一步确认。"

固定回答逻辑：
1. 先判断用户问的是"本市招生"（包含统一招生和自主招生）还是"随迁子女招生"，如果用户没有说明招生类别，必须提醒：不同类别的招生代码、招生范围和是否招生可能不同，并按两类分别说明。
2. 回答时必须严格区分志愿名称和专业名称，不要混淆。
3. 提及志愿名称时，必须说明对应的招生代码和学制。
4. 若资料写明"不招"，必须直接说明该志愿不招生。
5. 若资料写明"限招浦东"，必须明确说明该志愿限招浦东，并非面向全市开放。

禁止事项：
- 不得混淆志愿名称和专业名称
- 不得混淆不同学制、不同类别的招生代码
- 不得遗漏面试要求或招生范围
- 不得自行补充未提供的政策内容

注意：
1.仅回答问题相关的内容，不要过度扩充。
2.推理过程中要思考用户的查询意图并回答。
3.回答风格要求简单、精确。`,
}

let activeAgentId = apiConfig.agentId
let loginPromise = null

// ── Session ID 管理 ──────────────────────────────────────────────
let currentSessionId = null

export function generateSessionId() {
  currentSessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
  return currentSessionId
}

export function getSessionId() {
  return currentSessionId
}

export function clearSessionId() {
  currentSessionId = null
}

export function setAgentId(id) {
  activeAgentId = id
}

/**
 * 默认登录，获取 token
 * POST /auth/c/doLogin
 */
function _detectClientId() {
  const p = window.location.pathname
  if (p.includes('mobile2') || p.startsWith('/aizs')) return 'mobile2'
  if (p.includes('mobile') || p.startsWith('/zsai')) return 'mobile1'
  return 'pc'
}

export async function doLogin() {
  if (loginPromise) return loginPromise
  loginPromise = (async () => {
    try {
      const clientId = _detectClientId()
      const res = await axios.post('https://hxai.sundeinfo.com/tts-api/do-login', { clientId }, {
        headers: { 'Content-Type': 'application/json' }
      })
      const body = res.data
      console.log('Login response:', JSON.stringify(body))
      const token = body.data?.token || body.token || body.data
      if (token) {
        apiConfig.token = token
        apiConfig.rawToken = body?.data || ''
        console.log('Login success, token:', token)
        return token
      }
      console.warn('Login response missing token:', body)
      return null
    } catch (err) {
      console.error('Login Error:', err)
      loginPromise = null
      return null
    }
  })()
  return loginPromise
}

export function getActiveAgentId() {
  return activeAgentId
}

function getCommonHeaders() {
  const headers = { 'Content-Type': 'application/json' }
  if (apiConfig.token) headers['Token'] = apiConfig.token
  if (apiConfig.forwardedHost) headers['Gj-Forwarded-Host'] = apiConfig.forwardedHost
  return headers
}

// 创建 axios 实例
const service = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: 15000,
})

service.interceptors.request.use(config => {
  const headers = getCommonHeaders()
  Object.assign(config.headers, headers)
  return config
})

function getConversationId() {
  let convId = localStorage.getItem('chat_conversation_id')
  if (!convId) {
    convId = 'kmc' + Date.now()
    localStorage.setItem('chat_conversation_id', convId)
  }
  return convId
}

let cachedEventId = null

/**
 * 动态生成 eventId，每次新建对话时调用
 * GET /api/ai/common/genEventId?agentId=xxx
 */
export async function fetchEventId() {
  const { eventAgentId, apiConfigured } = apiConfig
  if (!apiConfigured || !eventAgentId) return ''
  try {
    const res = await service.get('/api/ai/common/genEventId', {
      params: { agentId: eventAgentId }
    })
    const body = res.data
    if (body.code === 200 && body.data) {
      cachedEventId = body.data
      return cachedEventId
    }
    console.warn('genEventId unexpected response:', body)
    return ''
  } catch (err) {
    console.error('genEventId Error:', err)
    return ''
  }
}

/**
 * 获取当前 eventId，没有则先请求生成
 */
async function getEventId() {
  if (cachedEventId) return cachedEventId
  return await fetchEventId()
}

/**
 * 清除 eventId 缓存（新建对话时调用）
 */
export function clearEventId() {
  cachedEventId = null
}

// ── 敏感词 ──────────────────────────────────────────────────────────
let cachedSensitiveWords = null

/**
 * 获取敏感词列表（带缓存，只请求一次）
 * GET /api/client/sensitivewords/list
 * 返回数组，每项包含敏感词和预设回复 triggerContent
 * @returns {Promise<Array<{word: string, triggerContent: string, ...}>>}
 */
export async function fetchSensitiveWords() {
  if (cachedSensitiveWords) return cachedSensitiveWords
  try {
    const res = await service.get('/api/client/sensitivewords/list')
    const body = res.data
    if (body.code === 200 && body.data) {
      cachedSensitiveWords = Array.isArray(body.data) ? body.data : []
      return cachedSensitiveWords
    }
    return []
  } catch (err) {
    console.error('fetchSensitiveWords Error:', err)
    return []
  }
}

/**
 * 检测文本是否包含敏感词
 * @param {string} text 待检测文本
 * @returns {Promise<{hit: boolean, word: string|null, triggerContent: string|null}>}
 */
export async function checkSensitiveWords(text) {
  const list = await fetchSensitiveWords()
  if (!list.length) return { hit: false, word: null, triggerContent: null }
  for (const item of list) {
    const keyword = item.word || item.keyword || item.name || (typeof item === 'string' ? item : '')
    if (keyword && text.includes(keyword)) {
      return { hit: true, word: keyword, triggerContent: item.triggerContent || null }
    }
  }
  return { hit: false, word: null, triggerContent: null }
}

/**
 * 获取历史对话记录
 * GET /api/client/answerhistory/get
 */
export async function getAnswerHistory() {
  if (!apiConfig.apiConfigured) return []
  try {
    const res = await service.get('/api/client/answerhistory/get', {
      params: { agentId: apiConfig.agentId }
    })
    const body = res.data
    if (body.code === 200 && body.data) {
      return Array.isArray(body.data) ? body.data : []
    }
    return []
  } catch (err) {
    console.error('getAnswerHistory Error:', err)
    return []
  }
}

/**
 * 请求答案（旧接口，暂时保留）
 * @param {string} question
 * @returns {Promise<string>}
 */
export async function getAnswer(question) {
  const { chatPath } = apiConfig

  try {
    const eventId = await getEventId()
    const payload = {
      agentId: activeAgentId,
      conversationId: getConversationId(),
      eventId,
      query: question.trim()
    }
    const res = await service.post(chatPath, payload)
    const body = res.data
    if (body.answer != null) return body.answer
    if (body.data && body.data.answer != null) return body.data.answer
    if (body.msg) return body.msg
    throw new Error('接口返回数据格式异常')
  } catch (err) {
    console.error('API Error:', err)
    throw err
  }
}

const ALL_CANDIDATE_QUESTIONS = [
'哪些学生可以报考？',
'考试及录取方式如何设置？',
'学校中本贯通（七年制）有哪个专业？',
'学校中高职贯通（五年制）有哪些专业？',
'学校中职三年制有哪些专业？',
'学校哪些专业需要提前面试？',
'预约面试时，是否需要对心仪的面试专业做选择？',
'面试需要多久？',
'面试的安排是怎样的？',
'面试时着装需要注意些什么？',
'报考面试专业的要求有哪些？',
'报考非面试专业有什么要求？',
'所有专业都是男女兼招吗?都是全市招生吗？',
'哪些学生可以报考？',
'考试及录取方式如何设置？',
'学校中本贯通（七年制）有哪个专业？',
'学校中高职贯通（五年制）有哪些专业？',
'学校中职三年制有哪些专业？',
'学校哪些专业需要提前面试？',
'预约面试时，是否需要对心仪的面试专业做选择？',
'面试需要多久？',
'面试的安排是怎样的？',
'面试时着装需要注意些什么？',
'报考面试专业的要求有哪些？',
'报考非面试专业有什么要求？',
'所有专业都是男女兼招吗？都是全市招生吗？',
'2025年学校录取各专业的最高分和最低分是多少？',
'录取总分构成？',
'学校学费如何收取？是否有助学政策？',
'学校提供住宿吗？',
'学校作息时间是如何安排的？',
'学校是否推荐学生实习及就业？单位有哪些？',
'中专毕业后，是否可以继续提升学历，报考大学本科或专科？',
'贯通类学生升学是否需要参加统一考试？',
'随迁子女可以报考中本贯通吗？',
'如果考入了三年制的专业，可以继续升学吗？',
'考取五年制中高职贯通，可以继续升本科吗？',
'如果考取七年制中本贯通、五年制中高职贯通，可以在就读期间换专业吗？',
'限招浦东新区的专业有哪些？',
'哪些专业不需要面试？',
'中本、中高职各专业就业/升学率高吗？',
'中本贯通专业和中高职贯通专业就读地点？',
'面试需携带《2026年上海市高中阶段学校招生考试信息确认表》/《2026年上海市全日制普通中等职业学校自主招收本市初中应届毕业生中来沪人员随迁子女招生考试信息确认表》复印件在哪里领取？',
'学生每月餐费如何计算？',
'住宿生一学期住宿费多少？',
'三年制学生参加三校生高考录取本科率？',
'住宿生床的尺寸？',
'面试当天学校提供停车吗？',
'面试当天几点到？',
'面试是单独一个一个面的吗？',
'面试当天可以带手机吗？',
'近视可以来报考空乘相关专业的中本贯通和中高职贯通吗？',
'面试当天可以戴框架眼镜吗？会影响考官的第一印象吗？',
'女生面试可以穿连裤袜吗？',
'女生面试可以化淡妆吗？',
'航空乘管、航空空乘、航空服务专业有什么区别？',
'面试当天需要才艺（舞蹈、乐器、球类）展示吗？',
'来参加中本贯通面试需要提前去培训机构上面试培训班吗？',
'面试需要提前些什么介绍内容？',
'面试只通过了3年制航空服务，本人一模二模分数还不错，还能报中本贯通和中高职贯通吗？',
'在学校报名的面试系统里选择自己的所在区后找不到自己的学校怎么办？',
'面试报名系统里注册时需要填的考生报名号是什么？',
'学校男女宿舍的住宿都是几人间？',
'住宿生寝室有独立卫生间和淋浴房吗？',
'住宿生放学后可以外出吗？',
'有才艺展示想要面试航空运输262603（艺术体育特长生，3年制）还需要额外带乐器和奖状吗？',
'随迁子女怎么界定？',
'中本贯通专业介绍？',
'中高职贯通专业介绍？',
'三年制专业介绍？',
]

const DASHSCOPE_URL = 'https://hxai.sundeinfo.com/tts-api/recommend'

function normalizeText(q) {
  return (q || '').replace(/[？?！!。.，,、：:；;\s]/g, '')
}

/**
 * 使用大模型从固定问题列表中挑选合适的推荐问题
 * @param {Array} history 对话历史 [{role: 'user', content: '...'}, {role: 'assistant', content: '...'}]
 * @returns {Promise<string[]>} 推荐问题列表，无合适则返回空数组
 */
export async function getRecommendedQuestions(history) {
  if (!history || history.length === 0) return []

  const alreadyAsked = new Set(
    history.filter(m => m.role === 'user').map(m => normalizeText(m.content))
  )
  const candidates = ALL_CANDIDATE_QUESTIONS.filter(q => !alreadyAsked.has(normalizeText(q)))
  if (candidates.length === 0) return []

  const recentHistory = history.slice(-6)

  const systemPrompt = `你是一个招生咨询助手。根据下方的对话历史，从候选问题列表中挑选最多3个用户最可能感兴趣的后续问题。

候选问题列表：
${candidates.map((q, i) => `${i + 1}. ${q}`).join('\n')}

要求：
1. 只能从上面的候选列表中选择，不要自创问题。
2. 选出与当前对话主题最相关、用户最可能想继续追问的问题。和用户已提出问题语义相同的不要推荐。
3. 如果候选列表中没有与当前对话相关的合适问题，返回空数组 []。
4. 直接返回 JSON 数组，内容是选中的问题原文，例如：["问题1", "问题2"]。
5. 不要包含任何解释或多余文字。`

  try {
    const response = await axios.post(DASHSCOPE_URL, {
      model: 'qwen-plus',
      messages: [
        { role: 'system', content: systemPrompt },
        ...recentHistory
      ]
    }, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 8000
    })

    const content = response.data.choices[0].message.content
    try {
      const match = content.match(/\[.*\]/s)
      if (match) {
        const parsed = JSON.parse(match[0])
        const result = []
        for (const q of parsed) {
          const found = candidates.find(c => normalizeText(c) === normalizeText(q))
          if (found && !result.includes(found)) result.push(found)
        }
        return result.slice(0, 3)
      }
    } catch (e) {
      console.warn('Failed to parse recommended questions:', content)
    }
    return []
  } catch (err) {
    console.error('getRecommendedQuestions Error:', err)
    return []
  }
}

/**
 * 流式请求答案 (SSE) — 新接口
 * @param {string} question
 * @param {object} options { onMessage, onDone, onError }
 * @returns {object} { abort: Function }
 */
export function getAnswerStream(question, { onMessage, onDone, onError, sessionId }) {
  const controller = new AbortController()

  const startFetch = async () => {
    try {
      const payload = {
        assistant_id: newApiConfig.assistantId,
        query: question.trim(),
        session_id: sessionId || currentSessionId || '',
        token: apiConfig.rawToken || '',
        userInfo: {
          outerUserName: '',
          outerUserRole: ''
        },
        system_prompt: newApiConfig.systemPrompt,
        backend_url: newApiConfig.backendUrl,
      }

      const response = await fetch(newApiConfig.streamPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Gj-Forwarded-Host': newApiConfig.forwardedHost,
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let done = false
      let accumulatedText = ''
      let lineBuffer = ''
      let sources = []

      while (!done) {
        const { value, done: doneReading } = await reader.read()
        done = doneReading
        const chunkValue = decoder.decode(value, { stream: !done })
        lineBuffer += chunkValue

        const normalizedBuffer = lineBuffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
        const lines = normalizedBuffer.split('\n')
        lineBuffer = lines.pop() || ''

        let hasUpdate = false

        const processJson = (data) => {
          if (!data || typeof data !== 'object') return
          const potentialSources = data.matches || data.sources || data.source_documents || data.chunks || data.nodes || data.docs
          if (potentialSources && Array.isArray(potentialSources)) {
            sources = potentialSources
            hasUpdate = true
          }
          const currentAnswer = data.answer || data.content || (data.choices?.[0]?.delta?.content) || (data.delta?.content)
          if (currentAnswer !== undefined) {
            const newText = String(currentAnswer)
            if (accumulatedText && newText.startsWith(accumulatedText)) {
              accumulatedText = newText
            } else {
              accumulatedText += newText
            }
            hasUpdate = true
          }
        }

        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine) continue
          if (trimmedLine.startsWith('data:')) {
            const dataStr = trimmedLine.replace(/^data:\s*/, '').trim()
            if (dataStr === '[DONE]') {
              done = true
              break
            }
            try {
              processJson(JSON.parse(dataStr))
            } catch (e) {
              const matches = dataStr.match(/\{.*?\}(?=\{|$)/g)
              if (matches) {
                matches.forEach(m => { try { processJson(JSON.parse(m)) } catch(err) {} })
              } else if (!dataStr.startsWith('{')) {
                accumulatedText += dataStr
                hasUpdate = true
              }
            }
          } else {
            try {
              processJson(JSON.parse(trimmedLine))
            } catch (e) {
              const matches = trimmedLine.match(/\{.*?\}(?=\{|$)/g)
              if (matches) {
                matches.forEach(m => { try { processJson(JSON.parse(m)) } catch(err) {} })
              }
            }
          }
        }
        if (hasUpdate) onMessage(accumulatedText, sources)
      }
      onDone()
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Stream aborted')
      } else {
        console.error('Stream Error:', err)
        onError(err)
      }
    }
  }

  startFetch()
  return { abort: () => controller.abort() }
}

// ── 会话标题生成 ──────────────────────────────────────────────────
/**
 * 调用 title_rewrite 接口为本轮对话生成简短标题（临时直连）
 * @param {string} query  用户问题
 * @param {string} answer 机器人回答
 * @returns {Promise<string>} 生成的标题，失败时回退为截取问题前 20 字
 */
export async function rewriteTitle(query, answer) {
  try {
    const res = await fetch(newApiConfig.titleRewritePath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Gj-Forwarded-Host': newApiConfig.forwardedHost,
      },
      body: JSON.stringify({ query, answer }),
    })
    if (!res.ok) throw new Error(`title_rewrite ${res.status}`)
    const data = await res.json()
    return data.label || data.title || data.data?.label || data.data?.title || data.data || query.slice(0, 20)
  } catch (err) {
    console.error('rewriteTitle Error:', err)
    return query.slice(0, 20)
  }
}

// ── 存入历史记录 ──────────────────────────────────────────────────

function genId() {
  return Date.now().toString() + Math.random().toString(36).slice(2, 8)
}

/**
 * 调用 devApi/client/answerhistory/add 保存一条问答记录
 * @param {object} params
 *   chatId       - 会话 id（即 sessionId）
 *   name         - 会话名称（title_rewrite 生成的标题）
 *   sceneId      - 智能体 id
 *   question     - 用户问题
 *   questionTime - 用户提问时间戳 (ms)
 *   content      - 大模型回复内容
 *   contentTime  - 大模型回复时间戳 (ms)
 *   documents    - 文件溯源列表
 */
export async function addAnswerHistory({
  chatId,
  name,
  sceneId,
  question,
  questionTime,
  content,
  contentTime,
  documents,
}) {
  try {
    const payload = {
      chatId,
      name: name || question.slice(0, 20),
      sceneId: sceneId || activeAgentId,
      questionId: genId(),
      question,
      questionTime,
      contentId: genId(),
      content,
      contentTime,
      documents: documents || [],
    }
    const res = await service.post('/api/client/answerhistory/add', payload)
    const body = res.data
    if (body.code === 200) {
      console.log('History saved successfully')
    } else {
      console.warn('addAnswerHistory unexpected response:', body)
    }
  } catch (err) {
    console.error('addAnswerHistory Error:', err)
  }
}

/* ========== 旧问答接口（已停用，仅保留代码） ========== */

/**
 * 流式请求答案 (SSE) — 旧接口，已停用
 * @deprecated 已切换至新接口 get_answer_stream_with_system_prompt
 */
function getAnswerStream_legacy(question, { onMessage, onDone, onError }) {
  const { baseURL, chatPath } = apiConfig

  const controller = new AbortController()
  
  const startFetch = async () => {
    try {
      const eventId = await getEventId()
      const payload = {
        agentId: activeAgentId,
        conversationId: getConversationId(),
        eventId,
        query: question.trim()
      }

      const response = await fetch(`${baseURL}${chatPath}`, {
        method: 'POST',
        headers: {
          ...getCommonHeaders(),
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      })

      if (!response.ok) throw new Error('Network response was not ok')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let done = false
      let accumulatedText = ''
      let lineBuffer = ''
      let sources = []

      while (!done) {
        const { value, done: doneReading } = await reader.read()
        done = doneReading
        const chunkValue = decoder.decode(value, { stream: !done })
        lineBuffer += chunkValue
        
        const normalizedBuffer = lineBuffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
        const lines = normalizedBuffer.split('\n')
        lineBuffer = lines.pop() || ''
        
        let hasUpdate = false
        
        const processJson = (data) => {
          if (!data || typeof data !== 'object') return
          const potentialSources = data.matches || data.sources || data.source_documents || data.chunks || data.nodes || data.docs
          if (potentialSources && Array.isArray(potentialSources)) {
            sources = potentialSources
            hasUpdate = true
          }
          const currentAnswer = data.answer || data.content || (data.choices?.[0]?.delta?.content) || (data.delta?.content)
          if (currentAnswer !== undefined) {
            const newText = String(currentAnswer)
            if (accumulatedText && newText.startsWith(accumulatedText)) {
              accumulatedText = newText
            } else {
              accumulatedText += newText
            }
            hasUpdate = true
          }
        }

        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine) continue
          if (trimmedLine.startsWith('data:')) {
            const dataStr = trimmedLine.replace(/^data:\s*/, '').trim()
            if (dataStr === '[DONE]') {
              done = true
              break
            }
            try {
              processJson(JSON.parse(dataStr))
            } catch (e) {
              const matches = dataStr.match(/\{.*?\}(?=\{|$)/g)
              if (matches) {
                matches.forEach(m => { try { processJson(JSON.parse(m)) } catch(err) {} })
              } else if (!dataStr.startsWith('{')) {
                accumulatedText += dataStr
                hasUpdate = true
              }
            }
          } else {
            try {
              processJson(JSON.parse(trimmedLine))
            } catch (e) {
              const matches = trimmedLine.match(/\{.*?\}(?=\{|$)/g)
              if (matches) {
                matches.forEach(m => { try { processJson(JSON.parse(m)) } catch(err) {} })
              }
            }
          }
        }
        if (hasUpdate) onMessage(accumulatedText, sources)
      }
      onDone()
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Stream aborted')
      } else {
        console.error('Stream Error:', err)
        onError(err)
      }
    }
  }

  startFetch()
  return { abort: () => controller.abort() }
}
